import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Search, BookOpen, FileText, Sparkles, MessageCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const KnowledgeBase = () => {
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Semantic search function
  const searchKnowledgeBase = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE}/admin/knowledge/search`, {
          params: { query, limit: 20 },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search failed:', error);
        toast.error('Search failed');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (searchQuery.trim()) {
      searchKnowledgeBase(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, searchKnowledgeBase]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/knowledge/documents`);
      setDocuments(response.data);
      setFilteredDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch knowledge documents');
    } finally {
      setLoading(false);
    }
  };

  const truncateContent = (content, maxLength = 300) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 text-yellow-800 px-1 rounded">
          {part}
        </span>
      ) : part
    );
  };

  const formatFileType = (fileType) => {
    switch (fileType) {
      case 'text/plain':
        return 'Text File';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'Word Document';
      default:
        return 'Document';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading knowledge base...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold font-display mb-4">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Knowledge Base
            </span>{' '}
            <span>📚</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            Expert child development resources curated by our team to help you understand your child's growth
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search knowledge base..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 form-input"
              data-testid="search-input"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
              </div>
            )}
          </div>
        </div>

        {/* AI Assistant CTA */}
        <Card className="glass border-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Need Personalized Advice?</h3>
                  <p className="text-slate-600">Chat with our AI assistant for answers specific to your child's development</p>
                </div>
              </div>
              <Link to="/chat">
                <Button className="btn-hover gradient-emerald text-white" data-testid="chat-assistant-button">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Ask AI Assistant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <div className="space-y-6">
          {documents.length === 0 ? (
            <Card className="glass border-0">
              <CardContent className="p-12 text-center">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">No Documents Available</h3>
                <p className="text-slate-500 mb-6">
                  The knowledge base is empty. Admin users can upload documents to build the knowledge base.
                </p>
                <Link to="/chat">
                  <Button className="btn-hover gradient-emerald text-white">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Try AI Assistant Instead
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (searchQuery.trim() && searchResults.length === 0 && !isSearching) ? (
            <Card className="glass border-0">
              <CardContent className="p-12 text-center">
                <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">No Results Found</h3>
                <p className="text-slate-500 mb-4">
                  No documents match your search query "{searchQuery}"
                </p>
                <Button 
                  onClick={() => setSearchQuery('')}
                  variant="outline"
                  className="btn-hover"
                >
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6" data-testid="documents-list">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-slate-800">
                  {searchQuery.trim() 
                    ? `Search Results (${searchResults.length > 0 ? searchResults.length : documents.length})`
                    : `All Documents (${documents.length})`
                  }
                </h2>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {(searchQuery.trim() && searchResults.length > 0 ? searchResults : documents).map((doc, index) => {
                  // Handle search results (different structure)
                  const docTitle = doc.title || 'Unknown';
                  const docContent = doc.chunk || doc.content || '';
                  const docId = doc.id || `search-result-${index}`;
                  const isSearchResult = searchQuery.trim() && searchResults.length > 0;
                  
                  return (
                  <Card 
                    key={docId} 
                    className={`glass border-0 card-hover cursor-pointer transition-all ${
                      selectedDocument?.id === docId ? 'ring-2 ring-emerald-500' : ''
                    }`}
                    onClick={() => setSelectedDocument(selectedDocument?.id === docId ? null : { id: docId, ...doc })}
                    data-testid={`document-${docTitle}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <CardTitle className="text-lg font-semibold text-slate-800 truncate">
                              {highlightSearchTerm(docTitle, searchQuery)}
                            </CardTitle>
                            <div className="flex items-center space-x-2 mt-1">
                              {!isSearchResult && (
                                <>
                                  <Badge variant="secondary" className="text-xs">
                                    {formatFileType(doc.file_type)}
                                  </Badge>
                                  <span className="text-xs text-slate-500">
                                    {new Date(doc.uploaded_at).toLocaleDateString()}
                                  </span>
                                </>
                              )}
                              {isSearchResult && doc.relevance && (
                                <Badge variant={doc.relevance === 'high' ? 'default' : 'secondary'} className="text-xs">
                                  {doc.relevance === 'high' ? 'Highly Relevant' : 'Relevant'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-slate-500 hover:text-slate-700 flex-shrink-0"
                        >
                          {selectedDocument?.id === docId ? 'Collapse' : 'Expand'}
                        </Button>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="text-sm text-slate-600 line-clamp-3">
                        {highlightSearchTerm(truncateContent(docContent, 200), searchQuery)}
                      </div>
                      
                      {selectedDocument?.id === docId && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <h4 className="font-medium text-slate-800 mb-3">Full Content:</h4>
                          <div className="text-sm text-slate-700 leading-relaxed max-h-96 overflow-y-auto bg-white/50 p-4 rounded-lg border border-emerald-200/50">
                            {highlightSearchTerm(docContent, searchQuery)}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
                        <span className="text-xs text-slate-500">
                          {docContent.length.toLocaleString()} characters
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="btn-hover text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDocument(selectedDocument?.id === docId ? null : { id: docId, ...doc });
                          }}
                        >
                          {selectedDocument?.id === docId ? 'Show Less' : 'Read More'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-slate-800 flex items-center space-x-2">
              <BookOpen className="w-6 h-6 text-emerald-600" />
              <span>Frequently Asked Questions</span>
            </CardTitle>
            <CardDescription>
              Common questions about child development
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="faq-section">
              <div className="p-4 bg-white/50 rounded-lg border border-emerald-200/50">
                <h4 className="font-medium text-slate-800 mb-2">When should my child start walking?</h4>
                <p className="text-sm text-slate-600">Most children take their first steps between 9-18 months, with the average being around 12-15 months.</p>
              </div>
              
              <div className="p-4 bg-white/50 rounded-lg border border-emerald-200/50">
                <h4 className="font-medium text-slate-800 mb-2">How can I encourage language development?</h4>
                <p className="text-sm text-slate-600">Talk to your child frequently, read together daily, and respond to their attempts at communication.</p>
              </div>
              
              <div className="p-4 bg-white/50 rounded-lg border border-emerald-200/50">
                <h4 className="font-medium text-slate-800 mb-2">What are important motor skill milestones?</h4>
                <p className="text-sm text-slate-600">Key milestones include rolling over (4-6 months), sitting up (6-8 months), and walking (12-15 months).</p>
              </div>
              
              <div className="p-4 bg-white/50 rounded-lg border border-emerald-200/50">
                <h4 className="font-medium text-slate-800 mb-2">How do I know if development is on track?</h4>
                <p className="text-sm text-slate-600">Regular pediatric checkups and milestone tracking help ensure healthy development. Every child develops at their own pace.</p>
              </div>
            </div>
            
            <div className="text-center mt-6">
              <Link to="/chat">
                <Button className="btn-hover gradient-emerald text-white">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Ask More Questions
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KnowledgeBase;
